import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CheckboxField from 'components/forms/checkboxField/CheckboxField';
import PropertySelectField from 'components/forms/domain/property/PropertySelectField';
import TextField from 'components/forms/textField/TextField';
import LoadingPlaceholder from 'components/loader/LoadingPlaceholder';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import { getUnitType } from 'utils/lead/leadUtils';
import useFetchAllPropertiesCalendarTools from '../../useFetchAllPropertiesCalendarTools';
import {
  CalendarExportFieldsContainer,
  CheckboxsContainer,
} from './CalendarExportSection.styles';

const getExportCalendarUrl = ({
  propertyUid,
  includeTentativeReservations,
  includeGuestInformation,
  includeThirdPartyLeads,
  isUnit,
}: {
  propertyUid: string;
  includeTentativeReservations: boolean;
  includeGuestInformation: boolean;
  includeThirdPartyLeads: boolean;
  isUnit: boolean;
}) => {
  const url = `${window.location.origin}/api/public/calendars/${
    isUnit ? 'units' : 'properties'
  }/${propertyUid}/calendar.ics`;
  const params = new URLSearchParams();

  params.append('showHold', includeTentativeReservations ? 'true' : 'false');

  if (includeGuestInformation) {
    params.append('igi', 'true');
  }

  if (includeThirdPartyLeads) {
    params.append('itp', 'true');
  }

  return `${url}?${params.toString()}`;
};

const CalendarExportFields = () => {
  const { t } = useTranslation();
  const { allProperties, isFetchingProperties } =
    useFetchAllPropertiesCalendarTools();
  const { watch } = useFormContext();
  const { getCopyAddonIcon } = useCopyToClipboard();

  const selectedPropertyUid = watch('propertyUid');
  const includeTentativeReservations = watch('includeTentativeReservations');
  const includeGuestInformation = watch('includeGuestInformation');
  const includeThirdPartyLeads = watch('includeThirdPartyLeads');
  const unitType = getUnitType(allProperties || [], selectedPropertyUid);
  const exportUrl = getExportCalendarUrl({
    propertyUid: selectedPropertyUid,
    includeTentativeReservations: includeTentativeReservations || false,
    includeGuestInformation: includeGuestInformation || false,
    includeThirdPartyLeads: includeThirdPartyLeads || false,
    isUnit: !!unitType,
  });

  const isPropertySelected = !!selectedPropertyUid;

  return (
    <CalendarExportFieldsContainer>
      {isFetchingProperties ? (
        <LoadingPlaceholder linesCount={1.2} width="100%" />
      ) : (
        <PropertySelectField
          name="propertyUid"
          properties={allProperties ?? []}
          emptyValueLabel={t(
            'pageAutomations.calendarTools.calendarExportSection.selectProperty',
          )}
          enableHotels={false}
          enableUnitTypes={false}
          colSmInput={12}
        />
      )}
      {isPropertySelected && (
        <>
          <TextField
            key={exportUrl}
            name="exportUrl"
            value={exportUrl}
            disabled
            colSmInput={12}
            rightAddons={[getCopyAddonIcon(exportUrl)]}
          />
          <CheckboxsContainer>
            <CheckboxField name="includeTentativeReservations" defaultChecked>
              {t(
                'pageAutomations.calendarTools.calendarExportSection.includeTentativeReservations',
              )}
            </CheckboxField>
            <CheckboxField name="includeGuestInformation">
              {t(
                'pageAutomations.calendarTools.calendarExportSection.includeGuestInformation',
              )}
            </CheckboxField>
            <CheckboxField name="includeThirdPartyLeads">
              {t(
                'pageAutomations.calendarTools.calendarExportSection.includeThirdPartyLeads',
              )}
            </CheckboxField>
          </CheckboxsContainer>
        </>
      )}
    </CalendarExportFieldsContainer>
  );
};

export default CalendarExportFields;
